import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Newspaper, Pencil, Trash2, Upload, X } from "lucide-react";
import { api } from "../../api";
import { useApp } from "../../store";

const inputCls =
  "w-full bg-white/[0.05] border border-white/15 focus:border-[var(--accent)] focus:outline-none px-3 py-2.5 text-sm transition-colors duration-300";

const EMPTY = { title_fr: "", title_en: "", body_fr: "", body_en: "", image: "" };

export const NewsManager = () => {
  const { t } = useApp();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () =>
    api.get("/news").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
    } catch {
      toast.error(t("admin_save_error"));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      if (editingId) await api.put(`/admin/news/${editingId}`, form);
      else await api.post("/admin/news", form);
      toast.success(t("admin_saved"));
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch {
      toast.error(t("admin_save_error"));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({ title_fr: item.title_fr, title_en: item.title_en, body_fr: item.body_fr, body_en: item.body_en, image: item.image || "" });
  };

  const remove = async (item) => {
    await api.delete(`/admin/news/${item.id}`);
    load();
  };

  const canSave = form.title_fr.trim().length >= 2 && form.body_fr.trim().length >= 2;

  return (
    <div data-testid="admin-news-panel" className="flex flex-col gap-8">
      <div className="border border-white/10 bg-white/[0.02] p-6">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
          {editingId ? t("admin_editing") : t("admin_news_add")}
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <input data-testid="admin-news-title-fr" value={form.title_fr} onChange={set("title_fr")} placeholder={t("admin_news_title_fr")} maxLength={200} className={inputCls} />
          <input data-testid="admin-news-title-en" value={form.title_en} onChange={set("title_en")} placeholder={t("admin_news_title_en")} maxLength={200} className={inputCls} />
          <textarea data-testid="admin-news-body-fr" rows={4} value={form.body_fr} onChange={set("body_fr")} placeholder={t("admin_news_body_fr")} maxLength={5000} className={`${inputCls} resize-y`} />
          <textarea data-testid="admin-news-body-en" rows={4} value={form.body_en} onChange={set("body_en")} placeholder={t("admin_news_body_en")} maxLength={5000} className={`${inputCls} resize-y`} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input data-testid="admin-news-image" value={form.image} onChange={set("image")} placeholder={t("admin_news_image")} maxLength={1000} className={`${inputCls} flex-1 min-w-[220px]`} />
          <label className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-[var(--accent)] hover:text-[var(--accent-soft)] px-4 py-2.5 rounded-full cursor-pointer transition-colors duration-300" data-testid="admin-news-upload">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? t("admin_uploading") : t("admin_upload")}
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" disabled={uploading}
              onChange={(e) => { if (e.target.files[0]) uploadImage(e.target.files[0]); e.target.value = ""; }} />
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <button data-testid="admin-news-save" onClick={save} disabled={busy || !canSave}
            className="inline-flex items-center gap-2 text-xs bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-60 text-white font-semibold px-5 py-3 rounded-full transition-colors duration-300">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Newspaper size={14} />}
            {busy ? t("admin_saving") : t("admin_save")}
          </button>
          {editingId && (
            <button data-testid="admin-news-cancel" onClick={() => { setEditingId(null); setForm(EMPTY); }}
              className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-white/40 px-4 py-3 rounded-full transition-colors duration-300">
              <X size={13} />
              {t("admin_cancel")}
            </button>
          )}
        </div>
      </div>

      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[var(--accent)]" size={26} /></div>
      ) : items.length === 0 ? (
        <p data-testid="admin-news-empty" className="text-white/45 py-12 text-center font-display italic text-xl">{t("news_empty")}</p>
      ) : (
        <div className="flex flex-col gap-4">
          {items.map((item) => (
            <article key={item.id} data-testid={`admin-news-item-${item.id}`} className="border border-white/10 bg-white/[0.02] p-6 flex gap-5">
              {item.image && <img src={item.image} alt="" className="w-24 h-24 object-contain bg-black/30 shrink-0 border border-white/10" />}
              <div className="flex-1 min-w-0">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-white/35">
                  {new Date(item.created_at).toLocaleDateString()}
                </p>
                <h3 className="mt-1 font-display font-semibold text-xl">{item.title_fr}</h3>
                <p className="mt-1 text-sm text-white/55 line-clamp-2">{item.body_fr}</p>
                <div className="mt-4 flex gap-2">
                  <button data-testid={`admin-news-edit-${item.id}`} onClick={() => startEdit(item)}
                    className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-white/40 px-3 py-2 rounded-full transition-colors duration-300">
                    <Pencil size={13} />
                    {t("admin_edit")}
                  </button>
                  <button data-testid={`admin-news-delete-${item.id}`} onClick={() => remove(item)}
                    className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-[var(--accent)] hover:text-[var(--accent-soft)] px-3 py-2 rounded-full transition-colors duration-300">
                    <Trash2 size={13} />
                    {t("admin_delete")}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
